import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  shoes: 'ri-footprint-line',
  hoodies: 'ri-t-shirt-2-line',
  tshirts: 'ri-t-shirt-air-line',
  jackets: 'ri-windy-line',
  pants: 'ri-scissors-line',
  headwear: 'ri-vip-crown-line',
  sets: 'ri-gift-line',
  underwear: 'ri-heart-3-line',
  jersey: 'ri-star-line',
  accessories: 'ri-vip-diamond-line',
  bags: 'ri-handbag-line',
  belts: 'ri-price-tag-3-line',
  perfumes: 'ri-drop-line',
  other: 'ri-grid-line',
};

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <section id="categories" className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-24">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="font-heading text-2xl font-semibold text-foreground-950 md:text-4xl">
          {t('categories_title')}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
          {t('categories_subtitle')}
        </p>
      </div>

      <div ref={ref} className="relative mx-auto mt-8 w-full max-w-md">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between rounded-lg border border-background-300/70 bg-white px-4 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center text-primary-600">
              <i className="ri-list-check-2" />
            </span>
            {t('categories_dropdown_label')}
          </span>
          <span className="w-4 h-4 flex items-center justify-center text-foreground-500">
            <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
          </span>
        </button>

        {open && (
          <div className="absolute z-30 mt-2 max-h-80 w-full overflow-y-auto rounded-lg border border-background-200 bg-white py-1">
            {categories.map((c) => (
              <a
                key={c.key}
                href={c.link}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-primary-50 cursor-pointer"
              >
                <span className="h-8 w-8 shrink-0 overflow-hidden rounded-md">
                  <img
                    src={c.image}
                    alt={t(`category_${c.key}`)}
                    title={`${t(`category_${c.key}`)} USFans Spreadsheet`}
                    className="h-full w-full object-cover object-top"
                  />
                </span>
                {t(`category_${c.key}`)}
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
        {categories.map((c) => (
          <a
            key={c.key}
            href={c.link}
            target="_blank"
            rel="noopener nofollow"
            className="group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-white transition-all hover:border-primary-300 cursor-pointer"
          >
            <div className="relative h-28 w-full overflow-hidden md:h-32">
              <img
                src={c.image}
                alt={t(`category_${c.key}`)}
                title={`${t(`category_${c.key}`)} USFans Spreadsheet`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="flex items-center gap-2 px-3 py-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className={`${categoryIcons[c.key]} text-sm`} />
              </span>
              <span className="text-sm font-semibold text-foreground-800 group-hover:text-primary-700">
                {t(`category_${c.key}`)}
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}